import { useRef, useCallback } from "react";
import { setAudioSessionType } from "../lib/audioSession";

// 24 kHz 16-bit mono: Gemini Live prefixes (and occasionally injects
// mid-turn) a 2-byte frame. Playing that as its own AudioBuffer is an
// impulse / beep. Skip anything under ~10 ms.
const MIN_PLAYBACK_SAMPLES = 240;

function base64ToFloat32(b64: string): Float32Array {
  const binaryStr = atob(b64);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  const even = bytes.byteLength & ~1;
  const int16 = new Int16Array(bytes.buffer, 0, even / 2);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) {
    float32[i] = int16[i] / (int16[i] < 0 ? 0x8000 : 0x7fff);
  }
  return float32;
}

export function useAudioPlayback(onChunkPlay?: (samples: Float32Array) => void, onDone?: () => void) {
  const contextRef = useRef<AudioContext | null>(null);
  const nodeRef = useRef<AudioWorkletNode | null>(null);
  const moduleLoadedRef = useRef(false);
  const ensuringRef = useRef<Promise<AudioWorkletNode | null> | null>(null);
  const pendingRef = useRef<Float32Array[]>([]);
  const isPlayingRef = useRef(false);
  const generationRef = useRef(0);
  const onChunkPlayRef = useRef(onChunkPlay);
  onChunkPlayRef.current = onChunkPlay;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  // Set by abort() to prevent onDone from firing after a manual barge-in.
  const suppressDoneRef = useRef(false);
  // Debounce timer: wait a short moment after playback ends for room echo to decay
  // before opening the mic. The elena_done gate (not timing) prevents premature triggers.
  const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const bindNode = useCallback((node: AudioWorkletNode) => {
    node.port.onmessage = (e: MessageEvent<{ type: string; generation?: number }>) => {
      if (e.data?.type !== "ended") return;
      if ((e.data.generation ?? 0) !== generationRef.current) return;
      isPlayingRef.current = false;
      if (!suppressDoneRef.current) {
        doneTimerRef.current = setTimeout(() => {
          doneTimerRef.current = null;
          if (!suppressDoneRef.current) onDoneRef.current?.();
        }, 200);
      }
    };
  }, []);

  const getContext = useCallback(() => {
    // No forced sampleRate: iOS Safari has a single hardware audio session, and
    // a context pinned to 24 kHz goes permanently silent once the mic worklet's
    // 16 kHz context (re)configures the session. The worklet resamples 24 kHz
    // PCM to whatever rate the context actually got.
    if (!contextRef.current || contextRef.current.state === "closed") {
      contextRef.current = new AudioContext();
      moduleLoadedRef.current = false;
      nodeRef.current = null;
    }
    const ctx = contextRef.current;
    if (ctx.state !== "running") {
      ctx.resume();
    }
    return ctx;
  }, []);

  const ensureNode = useCallback(async () => {
    if (nodeRef.current) return nodeRef.current;
    if (ensuringRef.current) return ensuringRef.current;
    const pending = (async (): Promise<AudioWorkletNode | null> => {
      try {
        const ctx = getContext();
        if (!moduleLoadedRef.current) {
          await ctx.audioWorklet.addModule("/pcm-player.worklet.js");
          moduleLoadedRef.current = true;
        }
        if (ctx.state === "closed") return null;
        const node = new AudioWorkletNode(ctx, "pcm-player", {
          numberOfOutputs: 1,
          outputChannelCount: [1],
        });
        bindNode(node);
        node.connect(ctx.destination);
        nodeRef.current = node;
        // A fresh processor starts at generation 0. Sync before any pcm/ended
        // or a post-abort node will play while ended is ignored forever.
        node.port.postMessage({ type: "generation", generation: generationRef.current });
        if (suppressDoneRef.current) {
          node.port.postMessage({ type: "abort", generation: generationRef.current });
          pendingRef.current = [];
        } else {
          for (const samples of pendingRef.current) {
            node.port.postMessage(
              { type: "pcm", samples, generation: generationRef.current },
              [samples.buffer],
            );
          }
          pendingRef.current = [];
        }
        return node;
      } catch (err) {
        moduleLoadedRef.current = false;
        nodeRef.current = null;
        pendingRef.current = [];
        isPlayingRef.current = false;
        console.error("Failed to init PCM player worklet:", err);
        return null;
      }
    })();
    ensuringRef.current = pending;
    try {
      return await pending;
    } finally {
      if (ensuringRef.current === pending) ensuringRef.current = null;
    }
  }, [bindNode, getContext]);

  const push = useCallback((samples: Float32Array) => {
    const node = nodeRef.current;
    if (node) {
      node.port.postMessage(
        { type: "pcm", samples, generation: generationRef.current },
        [samples.buffer],
      );
      return;
    }
    pendingRef.current.push(samples);
    void ensureNode();
  }, [ensureNode]);

  // Must be called synchronously from within a user-gesture handler (e.g. the
  // "Start" button's onClick) so iOS Safari doesn't leave the context
  // permanently suspended — it silently no-ops source.start() otherwise.
  const unlock = useCallback(() => {
    // iOS mutes Web Audio output when the ring/silent switch is on silent
    // (HTML media elements are exempt, which is why YouTube still has sound).
    // An explicit session type exempts us too; "playback" also selects the
    // loudspeaker route. The mic hook flips to "play-and-record" only while a
    // turn's mic is open. iOS 17+ / Safari-only API, harmless elsewhere.
    setAudioSessionType("playback");
    const ctx = getContext();
    const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start();
    void ensureNode();
  }, [ensureNode, getContext]);

  const enqueue = useCallback((b64: string) => {
    const float32 = base64ToFloat32(b64);
    if (float32.length < MIN_PLAYBACK_SAMPLES) return;
    suppressDoneRef.current = false;
    if (doneTimerRef.current) { clearTimeout(doneTimerRef.current); doneTimerRef.current = null; }
    onChunkPlayRef.current?.(float32);
    isPlayingRef.current = true;
    push(float32);
  }, [push]);

  // True while audio is actively playing OR within the post-playback echo-decay
  // window. The auto-mode watchdog uses this to tell "Elena still speaking / just
  // finished" apart from "genuinely idle, safe to open the mic".
  const isPlaying = useCallback(
    () => isPlayingRef.current || pendingRef.current.length > 0 || doneTimerRef.current !== null,
    [],
  );

  // Stops playback but deliberately keeps the AudioContext alive: on iOS Safari
  // a context can only be (re)started from a user gesture, so closing it here
  // would leave every subsequent turn permanently silent.
  const abort = useCallback(() => {
    suppressDoneRef.current = true;
    generationRef.current += 1;
    if (doneTimerRef.current) { clearTimeout(doneTimerRef.current); doneTimerRef.current = null; }
    pendingRef.current = [];
    try { nodeRef.current?.port.postMessage({ type: "abort", generation: generationRef.current }); } catch { /* node gone */ }
    isPlayingRef.current = false;
  }, []);

  return { enqueue, abort, isPlaying, unlock };
}
